import * as Dialog from '@radix-ui/react-dialog';
import { Lock, LockOpen, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { reactivateAccessRequestSchema, suspendAccessRequestSchema } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Textarea } from './form-primitives';
import { PillButton } from './PillButton';
import { extractErrorMessage } from '../../lib/errors';
import { useAuth } from '../../hooks/use-auth';
import { useReactivateAccess, useSuspendAccess } from '../../hooks/use-provisioning';

interface AccessOverrideActionsProps {
  clientId: string;
  clientStatus: string;
  /** Cliente tem charges vencidas — usado pro aviso vermelho ao reativar (AC6/FR-58.4). */
  hasOverdueDebt: boolean;
}

const MIN_JUSTIFICATION = 20;

export function AccessOverrideActions({
  clientId,
  clientStatus,
  hasOverdueDebt,
}: AccessOverrideActionsProps): JSX.Element | null {
  const { user } = useAuth();
  const [mode, setMode] = useState<'suspend' | 'reactivate' | null>(null);

  // AC5 — só admin/financeiro. Suporte/comercial não veem.
  const canManage = user?.role === 'admin' || user?.role === 'financeiro';
  if (!canManage) return null;

  const canSuspend = clientStatus === 'ativo' || clientStatus === 'inadimplente';
  const canReactivate = clientStatus === 'suspenso';
  if (!canSuspend && !canReactivate) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canSuspend && (
        <button
          type="button"
          onClick={() => setMode('suspend')}
          className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-bad/40 bg-bad/5 px-3 text-[12px] font-medium text-bad transition-colors hover:bg-bad/10"
        >
          <Lock size={13} strokeWidth={1.8} />
          Suspender acesso agora
        </button>
      )}
      {canReactivate && (
        <button
          type="button"
          onClick={() => setMode('reactivate')}
          className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border border-good/40 bg-good/5 px-3 text-[12px] font-medium text-good transition-colors hover:bg-good/10"
        >
          <LockOpen size={13} strokeWidth={1.8} />
          Reativar acesso agora
        </button>
      )}

      <OverrideModal
        mode={mode}
        clientId={clientId}
        hasOverdueDebt={hasOverdueDebt}
        onClose={() => setMode(null)}
      />
    </div>
  );
}

interface OverrideModalProps {
  mode: 'suspend' | 'reactivate' | null;
  clientId: string;
  hasOverdueDebt: boolean;
  onClose: () => void;
}

function OverrideModal({
  mode,
  clientId,
  hasOverdueDebt,
  onClose,
}: OverrideModalProps): JSX.Element {
  const [justification, setJustification] = useState('');
  const [error, setError] = useState<string | null>(null);
  const suspendMut = useSuspendAccess();
  const reactivateMut = useReactivateAccess();

  const open = mode !== null;
  const isSuspend = mode === 'suspend';
  const pending = suspendMut.isPending || reactivateMut.isPending;
  const trimmedLen = justification.trim().length;
  const tooShort = trimmedLen < MIN_JUSTIFICATION;

  function close() {
    onClose();
    setJustification('');
    setError(null);
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    const payload = { justification: justification.trim() };
    const schema = isSuspend ? suspendAccessRequestSchema : reactivateAccessRequestSchema;
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Justificativa inválida.');
      return;
    }
    try {
      if (isSuspend) {
        await suspendMut.mutateAsync({ clientId, body: payload });
      } else {
        const res = await reactivateMut.mutateAsync({ clientId, body: payload });
        if (res.data.warning) toast.warning(res.data.warning);
      }
      toast.success(
        `${isSuspend ? 'Suspensão' : 'Reativação'} iniciada. O acesso será atualizado em até 5 minutos.`,
      );
      close();
    } catch (err) {
      // AC7 — 409 (já no estado) vira toast claro; demais erros idem.
      toast.error(
        extractErrorMessage(
          err,
          isSuspend ? 'Falha ao suspender o acesso.' : 'Falha ao reativar o acesso.',
        ),
      );
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && close()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={cn(
            'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <Dialog.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
            'w-[min(520px,calc(100vw-32px))] max-h-[90dvh] overflow-y-auto',
            'rounded-[14px] border border-line bg-panel p-6 shadow-lg focus:outline-none',
          )}
        >
          <div className="mb-4 flex items-start justify-between gap-4">
            <Dialog.Title className="text-[16px] font-semibold tracking-[-0.005em] text-ink">
              {isSuspend ? 'Suspender acesso manualmente' : 'Reativar acesso manualmente'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Fechar"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <p className="mb-3 text-[12px] leading-relaxed text-muted">
            {isSuspend
              ? 'Esta ação suspenderá o acesso ao SaaS imediatamente. O cliente perderá o acesso assim que o job for processado. Continue?'
              : 'Esta ação reativará o acesso ao SaaS. O acesso será concedido assim que o job for processado.'}
          </p>

          {!isSuspend && hasOverdueDebt && (
            <p className="mb-3 rounded-[8px] border border-bad/30 bg-bad/5 p-2 text-[12px] font-medium text-bad">
              Atenção: este cliente possui débitos em aberto. A reativação manual registra que o
              operador assume o risco.
            </p>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
            <Field
              label={`Justificativa (mín. ${MIN_JUSTIFICATION} caracteres)`}
              required
              error={error ?? undefined}
            >
              {({ inputId }) => (
                <Textarea
                  id={inputId}
                  rows={3}
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  placeholder="Ex: cliente em negociação especial aprovada pela diretoria; acordo de pagamento em andamento."
                  hasError={Boolean(error)}
                />
              )}
            </Field>
            <div className="-mt-1 text-right text-[11px] text-muted-3 tnum">
              {trimmedLen}/{MIN_JUSTIFICATION}
            </div>
            <div className="mt-1 flex items-center justify-end gap-2">
              <PillButton type="button" onClick={close} disabled={pending}>
                Cancelar
              </PillButton>
              <PillButton type="submit" solid disabled={pending || tooShort}>
                {pending ? 'Processando…' : isSuspend ? 'Suspender' : 'Reativar'}
              </PillButton>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
